import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageNavigate } from './hooks/useLanguageNavigate';
import {
  ArrowRight,
  Calendar,
  ShoppingBag,
  Clock,
  Leaf,
  Heart,
  Users,
  Shield,
  Sparkles,
  ChefHat,
  Truck,
  Star,
  Check,
  Zap
} from 'lucide-react';

const HomeStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Noto+Sans+TC:wght@400;500;700;800&display=swap');

    :root {
      --color-black: #111;
      --color-white: #fff;
      --color-grey-600: #666;
      --color-grey-400: #999;
      --color-grey-200: #e5e5e5;
      --color-bg-alt: #fafafa;
      --color-green: #2F7D5E;
      --color-green-dark: #245F48;
      --font-main: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
      --shadow: 0 1px 3px rgba(0,0,0,0.08);
    }

    .ph-home {
      font-family: var(--font-main);
      background: var(--color-white);
      color: var(--color-black);
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }

    .ph-home * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .ph-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1.5rem;
    }

    /* ---- Hero ---- */
    .ph-hero {
      padding: 6rem 0 4rem;
    }

    .ph-hero-layout {
      display: flex;
      align-items: center;
      gap: 3rem;
    }

    .ph-hero-inner {
      max-width: 640px;
      flex: 1;
    }

    .ph-hero-image {
      flex: 0 0 420px;
      max-width: 420px;
    }

    .ph-hero-img {
      width: 100%;
      border-radius: 4px;
    }

    .ph-hero-title {
      font-family: var(--font-main);
      font-size: clamp(2.5rem, 5.5vw, 4rem);
      font-weight: 400;
      line-height: 1.1;
      letter-spacing: -0.02em;
      color: var(--color-black);
      margin-bottom: 1.5rem;
    }

    .ph-hero-title .highlight {
      color: var(--color-green);
    }

    .ph-hero-subtitle {
      font-size: 1.125rem;
      color: var(--color-grey-600);
      line-height: 1.7;
      margin-bottom: 2.5rem;
      max-width: 520px;
    }

    .ph-cta-group {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      margin-bottom: 3rem;
    }

    .ph-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.875rem 2rem;
      font-family: var(--font-main);
      font-size: 1rem;
      font-weight: 400;
      border-radius: 4px;
      border: 2px solid transparent;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      text-decoration: none;
    }

    .ph-btn:hover {
      transform: translateY(-2px);
    }

    .ph-btn-primary {
      background: var(--color-green);
      color: var(--color-white);
      border-color: var(--color-green);
    }

    .ph-btn-primary:hover {
      box-shadow: var(--shadow);
    }

    .ph-btn-secondary {
      background: var(--color-white);
      color: var(--color-green);
      border-color: var(--color-green);
    }

    .ph-btn-secondary:hover {
      box-shadow: var(--shadow);
    }

    .ph-trust-bar {
      display: flex;
      align-items: center;
      gap: 2rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--color-grey-200);
    }

    .ph-trust-item {
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }

    .ph-trust-icon {
      color: var(--color-green);
    }

    .ph-trust-text {
      font-size: 0.875rem;
      color: var(--color-grey-400);
      font-weight: 400;
    }

    /* ---- Section Header ---- */
    .ph-section-header {
      text-align: center;
      margin-bottom: 3.5rem;
    }

    .ph-section-label {
      display: inline-block;
      font-size: 0.8rem;
      font-weight: 400;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--color-green);
      margin-bottom: 0.75rem;
    }

    .ph-section-title {
      font-family: var(--font-main);
      font-size: clamp(1.75rem, 3.5vw, 2.5rem);
      font-weight: 400;
      color: var(--color-black);
      margin-bottom: 0.75rem;
      line-height: 1.2;
    }

    .ph-section-subtitle {
      font-size: 1.05rem;
      color: var(--color-grey-600);
      max-width: 560px;
      margin: 0 auto;
      line-height: 1.7;
    }

    /* ---- Services ---- */
    .ph-services {
      padding: 5rem 0;
      background: var(--color-bg-alt);
    }

    .ph-service-cards {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;
    }

    .ph-service-card {
      background: var(--color-white);
      border: 1px solid var(--color-grey-200);
      border-radius: 4px;
      padding: 2.5rem;
      cursor: pointer;
      transition: border-color 0.2s ease, transform 0.2s ease;
    }

    .ph-service-card:hover {
      border-color: var(--color-green);
      transform: translateY(-2px);
    }

    .ph-service-icon-wrapper {
      width: 56px;
      height: 56px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-green);
      border-radius: 4px;
      color: var(--color-white);
      margin-bottom: 1.5rem;
    }

    .ph-service-title {
      font-family: var(--font-main);
      font-size: 1.5rem;
      font-weight: 400;
      color: var(--color-black);
      margin-bottom: 0.75rem;
    }

    .ph-service-desc {
      font-size: 0.95rem;
      color: var(--color-grey-600);
      line-height: 1.7;
      margin-bottom: 1.5rem;
    }

    .ph-service-features {
      display: grid;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
    }

    .ph-service-feature {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;
      color: var(--color-grey-600);
    }

    .ph-service-feature-icon {
      color: var(--color-green);
      flex-shrink: 0;
    }

    .ph-service-cta {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.95rem;
      font-weight: 400;
      color: var(--color-green);
    }

    /* ---- Benefits ---- */
    .ph-benefits {
      padding: 5rem 0;
    }

    .ph-benefits-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 2rem;
    }

    .ph-benefit {
      text-align: center;
      padding: 2rem 1.5rem;
    }

    .ph-benefit-icon-wrapper {
      width: 56px;
      height: 56px;
      margin: 0 auto 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-green);
      border-radius: 50%;
      color: var(--color-white);
    }

    .ph-benefit-title {
      font-family: var(--font-main);
      font-size: 1.1rem;
      font-weight: 400;
      color: var(--color-black);
      margin-bottom: 0.5rem;
    }

    .ph-benefit-desc {
      font-size: 0.9rem;
      color: var(--color-grey-600);
      line-height: 1.7;
    }

    /* ---- Process ---- */
    .ph-process {
      padding: 5rem 0;
      background: var(--color-bg-alt);
    }

    .ph-steps {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;
      position: relative;
    }

    .ph-steps::before {
      content: '';
      position: absolute;
      top: 28px;
      left: 12%;
      right: 12%;
      height: 1px;
      background: var(--color-grey-200);
      z-index: 0;
    }

    .ph-step {
      position: relative;
      z-index: 1;
      text-align: center;
    }

    .ph-step-number {
      width: 56px;
      height: 56px;
      margin: 0 auto 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-white);
      border: 2px solid var(--color-grey-200);
      border-radius: 50%;
      font-family: var(--font-main);
      font-size: 1.25rem;
      font-weight: 400;
      color: var(--color-green);
    }

    .ph-step-title {
      font-family: var(--font-main);
      font-size: 1.05rem;
      font-weight: 400;
      color: var(--color-black);
      margin-bottom: 0.375rem;
    }

    .ph-step-desc {
      font-size: 0.875rem;
      color: var(--color-grey-600);
      line-height: 1.6;
    }

    /* ---- Testimonials ---- */
    .ph-testimonials {
      padding: 5rem 0;
      background: var(--color-green);
    }

    .ph-testimonials .ph-section-label {
      color: rgba(255,255,255,0.7);
    }

    .ph-testimonials .ph-section-title {
      color: var(--color-white);
    }

    .ph-testimonials .ph-section-subtitle {
      color: rgba(255,255,255,0.8);
    }

    .ph-testimonials-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }

    .ph-testimonial {
      background: var(--color-white);
      border-radius: 4px;
      padding: 2rem;
      transition: transform 0.2s ease;
    }

    .ph-testimonial:hover {
      transform: translateY(-2px);
    }

    .ph-testimonial-stars {
      display: flex;
      gap: 2px;
      margin-bottom: 1rem;
      color: #F59E0B;
    }

    .ph-testimonial-text {
      font-size: 0.95rem;
      color: var(--color-grey-600);
      line-height: 1.7;
      margin-bottom: 1.5rem;
    }

    .ph-testimonial-author {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .ph-testimonial-avatar {
      width: 40px;
      height: 40px;
      background: var(--color-green);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(--font-main);
      font-size: 0.875rem;
      font-weight: 400;
      color: var(--color-white);
    }

    .ph-testimonial-name {
      font-weight: 400;
      color: var(--color-black);
      font-size: 0.95rem;
    }

    .ph-testimonial-role {
      font-size: 0.8rem;
      color: var(--color-grey-400);
    }

    /* ---- Final CTA ---- */
    .ph-final-cta {
      padding: 5rem 0;
    }

    .ph-final-cta-box {
      background: var(--color-green);
      border-radius: 4px;
      padding: 4rem 2rem;
      text-align: center;
      color: var(--color-white);
    }

    .ph-final-cta-title {
      font-family: var(--font-main);
      font-size: clamp(1.75rem, 3.5vw, 2.5rem);
      font-weight: 400;
      margin-bottom: 0.75rem;
      line-height: 1.2;
    }

    .ph-final-cta-desc {
      font-size: 1.05rem;
      opacity: 0.9;
      margin-bottom: 2rem;
      line-height: 1.7;
    }

    .ph-final-cta-btn {
      background: var(--color-white);
      color: var(--color-green);
      border-color: var(--color-white);
      padding: 1rem 2.5rem;
      font-size: 1.05rem;
      font-weight: 400;
    }

    .ph-final-cta-btn:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    /* ---- Footer ---- */
    .ph-footer {
      padding: 2.5rem 0;
      text-align: center;
      color: var(--color-grey-400);
      font-size: 0.875rem;
      border-top: 1px solid var(--color-grey-200);
    }

    .ph-footer-brand {
      color: var(--color-green);
      font-weight: 400;
    }

    /* ---- Responsive ---- */
    @media (max-width: 1024px) {
      .ph-hero-layout {
        flex-direction: column;
      }
      .ph-hero-image {
        flex: none;
        max-width: 100%;
        width: 100%;
      }

      .ph-service-cards,
      .ph-benefits-grid,
      .ph-testimonials-grid {
        grid-template-columns: 1fr;
      }

      .ph-steps {
        grid-template-columns: repeat(2, 1fr);
      }

      .ph-steps::before {
        display: none;
      }
    }

    @media (max-width: 768px) {
      .ph-hero {
        padding: 4rem 0 3rem;
      }

      .ph-cta-group {
        flex-direction: column;
      }

      .ph-btn {
        width: 100%;
      }

      .ph-trust-bar {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
      }

      .ph-services,
      .ph-benefits,
      .ph-process,
      .ph-testimonials,
      .ph-final-cta {
        padding: 3.5rem 0;
      }

      .ph-steps {
        grid-template-columns: 1fr;
      }

      .ph-final-cta-box {
        padding: 3rem 1.5rem;
      }
    }

    /* ---- Accessibility ---- */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        transition-duration: 0.01ms !important;
      }
    }

    .ph-btn:focus-visible,
    .ph-service-card:focus-visible {
      outline: 2px solid var(--color-green);
      outline-offset: 3px;
    }
  `}</style>
);

const PeachesHomePage = () => {
  const navigate = useLanguageNavigate();
  const { t } = useTranslation('home');

  return (
    <>
      <HomeStyles />
      <div className="ph-home">
        {/* Hero */}
        <section className="ph-hero">
          <div className="ph-container">
            <div className="ph-hero-layout">
              <div className="ph-hero-inner">
                <h1 className="ph-hero-title">
                  {t('hero.title_line1')}<br />
                  {t('hero.title_line2')}<br />
                  <span className="highlight">{t('hero.title_highlight')}</span>
                </h1>

                <p className="ph-hero-subtitle">
                  {t('hero.subtitle_line1')}<br />
                  {t('hero.subtitle_line2')}
                </p>

                <div className="ph-cta-group">
                  <button
                    className="ph-btn ph-btn-primary"
                    onClick={() => navigate('/booking/single')}
                  >
                    <ShoppingBag size={20} />
                    <span>{t('hero.cta_single')}</span>
                    <ArrowRight size={20} />
                  </button>
                </div>

                <div className="ph-trust-bar">
                  <div className="ph-trust-item">
                    <Star size={16} fill="currentColor" className="ph-trust-icon" />
                    <span className="ph-trust-text">{t('hero.trust_rating')}</span>
                  </div>
                  <div className="ph-trust-item">
                    <Shield size={16} className="ph-trust-icon" />
                    <span className="ph-trust-text">{t('hero.trust_fresh')}</span>
                  </div>
                  <div className="ph-trust-item">
                    <Users size={16} className="ph-trust-icon" />
                    <span className="ph-trust-text">{t('hero.trust_customers')}</span>
                  </div>
                </div>
              </div>

              <div className="ph-hero-image">
                <img
                  src="/hero-meal.jpg"
                  alt="Baked Basa Filet on Rice"
                  className="ph-hero-img"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services - hidden for now */}
        {/* Benefits - hidden for now */}

        {/* Process */}
        <section className="ph-process">
          <div className="ph-container">
            <div className="ph-section-header">
              <div className="ph-section-label">{t('process.badge')}</div>
              <h2 className="ph-section-title">{t('process.title')}</h2>
              <p className="ph-section-subtitle">{t('process.subtitle')}</p>
            </div>

            <div className="ph-steps">
              <div className="ph-step">
                <div className="ph-step-number">1</div>
                <h3 className="ph-step-title">{t('process.step1_title')}</h3>
                <p className="ph-step-desc">{t('process.step1_desc')}</p>
              </div>
              <div className="ph-step">
                <div className="ph-step-number">2</div>
                <h3 className="ph-step-title">{t('process.step2_title')}</h3>
                <p className="ph-step-desc">{t('process.step2_desc')}</p>
              </div>
              <div className="ph-step">
                <div className="ph-step-number">3</div>
                <h3 className="ph-step-title">{t('process.step3_title')}</h3>
                <p className="ph-step-desc">{t('process.step3_desc')}</p>
              </div>
              <div className="ph-step">
                <div className="ph-step-number">4</div>
                <h3 className="ph-step-title">{t('process.step4_title')}</h3>
                <p className="ph-step-desc">{t('process.step4_desc')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials - hidden for now */}

        {/* Final CTA */}
        <section className="ph-final-cta">
          <div className="ph-container">
            <div className="ph-final-cta-box">
              <h2 className="ph-final-cta-title">{t('cta.title')}</h2>
              <p className="ph-final-cta-desc">
                {t('cta.desc_line1')}<br />
                {t('cta.desc_line2')}
              </p>
              <button
                className="ph-btn ph-final-cta-btn"
                onClick={() => navigate('/booking/single')}
              >
                <span>{t('cta.button')}</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="ph-footer">
          <div className="ph-container">
            <p style={{ marginBottom: '0.25rem' }}>
              <span className="ph-footer-brand">{t('footer.brand')}</span>
            </p>
            <p>{t('footer.copyright')}</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PeachesHomePage;
